import type { CalendarRange } from './calendar-item';

// Calendar arithmetic in the account's business timezone. A day value is the instant at which that
// timezone's civil day begins, so DST still produces 23- and 25-hour days.

export type CalendarView = 'day' | 'week' | 'month';

export const CALENDAR_VIEWS: readonly CalendarView[] = ['day', 'week', 'month'];

interface CivilDate {
  year: number;
  month: number;
  day: number;
}

const civilFormatters = new Map<string, Intl.DateTimeFormat>();
const instantFormatters = new Map<string, Intl.DateTimeFormat>();

function civilDate(at: number, timeZone?: string): CivilDate {
  if (!timeZone) {
    const date = new Date(at);
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
  }
  let formatter = civilFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' });
    civilFormatters.set(timeZone, formatter);
  }
  const parts = formatter.formatToParts(at);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return { year: read('year'), month: read('month'), day: read('day') };
}

function civilInstant({ year, month, day }: CivilDate, hour: number, minute: number, timeZone?: string): number {
  if (!timeZone) return new Date(year, month - 1, day, hour, minute).getTime();
  let formatter = instantFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    });
    instantFormatters.set(timeZone, formatter);
  }
  const target = Date.UTC(year, month - 1, day, hour, minute);
  let instant = target;
  for (let attempt = 0; attempt < 3; attempt++) {
    const parts = formatter.formatToParts(instant);
    const read = (type: string) => Number(parts.find((part) => part.type === type)?.value);
    const represented = Date.UTC(read('year'), read('month') - 1, read('day'), read('hour'), read('minute'), read('second'));
    const correction = target - represented;
    instant += correction;
    if (correction === 0) break;
  }
  return instant;
}

function civilMidnight(date: CivilDate, timeZone?: string): number {
  return civilInstant(date, 0, 0, timeZone);
}

export function atDayClock(day: number, minutes: number, timeZone?: string): number {
  const date = civilDate(day, timeZone);
  return civilInstant(date, 0, minutes, timeZone);
}

function shiftCivil(date: CivilDate, days: number): CivilDate {
  const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: shifted.getUTCDate() };
}

export function startOfDay(at: number, timeZone?: string): number {
  if (timeZone) return civilMidnight(civilDate(at, timeZone), timeZone);
  const date = new Date(at);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function addDays(day: number, days: number, timeZone?: string): number {
  if (timeZone) return civilMidnight(shiftCivil(civilDate(day, timeZone), days), timeZone);
  const date = new Date(day);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days).getTime();
}

export function addMonths(day: number, months: number, timeZone?: string): number {
  if (timeZone) {
    const date = civilDate(day, timeZone);
    const shifted = new Date(Date.UTC(date.year, date.month - 1 + months, 1));
    return civilMidnight({ year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: 1 }, timeZone);
  }
  const date = new Date(day);
  return new Date(date.getFullYear(), date.getMonth() + months, 1).getTime();
}

export function sameDay(a: number, b: number, timeZone?: string): boolean {
  return startOfDay(a, timeZone) === startOfDay(b, timeZone);
}

/** 0 = Sunday … 6 = Saturday, from the locale's own week where the runtime knows it. */
export function weekStartsOn(locale: string | undefined): number {
  try {
    const info = new Intl.Locale(locale ?? 'en-US') as Intl.Locale & {
      weekInfo?: { firstDay: number };
      getWeekInfo?: () => { firstDay: number };
    };
    const firstDay = info.getWeekInfo?.().firstDay ?? info.weekInfo?.firstDay;
    if (firstDay) return firstDay % 7;
  } catch {
    // An unknown tag falls through to the default.
  }
  return locale?.toLowerCase().startsWith('he') || locale?.toLowerCase().startsWith('en-us') ? 0 : 1;
}

export function startOfWeek(at: number, firstDay: number, timeZone?: string): number {
  const day = startOfDay(at, timeZone);
  const civil = civilDate(day, timeZone);
  const weekday = new Date(Date.UTC(civil.year, civil.month - 1, civil.day)).getUTCDay();
  const offset = (weekday - firstDay + 7) % 7;
  return addDays(day, -offset, timeZone);
}

/** The days a view draws, as midnights in the business time zone (`timeZone`). A month draws whole weeks, 5 or 6 of them. */
export function viewDays(view: CalendarView, anchor: number, firstDay: number, timeZone?: string): number[] {
  if (view === 'day') return [startOfDay(anchor, timeZone)];
  if (view === 'week') {
    const start = startOfWeek(anchor, firstDay, timeZone);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i, timeZone));
  }
  const first = civilDate(anchor, timeZone);
  const monthStart = civilMidnight({ ...first, day: 1 }, timeZone);
  const nextMonth = addMonths(monthStart, 1, timeZone);
  const days: number[] = [];
  for (let day = startOfWeek(monthStart, firstDay, timeZone); day < nextMonth || days.length % 7 !== 0; day = addDays(day, 1, timeZone)) {
    days.push(day);
  }
  return days;
}

export function viewRange(view: CalendarView, anchor: number, firstDay: number, timeZone?: string): CalendarRange {
  const days = viewDays(view, anchor, firstDay, timeZone);
  return { from: days[0], to: addDays(days[days.length - 1], 1, timeZone) };
}

/** The anchor one step back or forward: a day, a week or a month. */
export function stepAnchor(view: CalendarView, anchor: number, direction: -1 | 1, timeZone?: string): number {
  if (view === 'day') return addDays(startOfDay(anchor, timeZone), direction, timeZone);
  if (view === 'week') return addDays(startOfDay(anchor, timeZone), 7 * direction, timeZone);
  return addMonths(anchor, direction, timeZone);
}

/** The day a view lands on after a step: today when it is in view, else the view's first day. */
export function focusDayFor(view: CalendarView, anchor: number, firstDay: number, now: number, timeZone?: string): number {
  const days = viewDays(view, anchor, firstDay, timeZone);
  const today = startOfDay(now, timeZone);
  if (view === 'month') {
    const month = civilDate(anchor, timeZone).month;
    const inMonth = days.filter((day) => civilDate(day, timeZone).month === month);
    return inMonth.includes(today) ? today : inMonth[0];
  }
  return days.includes(today) ? today : days[0];
}

/** `YYYY-MM-DD` for the URL, in the business time zone (`timeZone`). */
export function toDayParam(day: number, timeZone?: string): string {
  const date = civilDate(day, timeZone);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.year}-${pad(date.month)}-${pad(date.day)}`;
}

export function fromDayParam(param: unknown, timeZone?: string): number | undefined {
  if (typeof param !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(param)) return undefined;
  const [year, month, day] = param.split('-').map(Number);
  const at = civilMidnight({ year, month, day }, timeZone);
  return Number.isFinite(at) ? at : undefined;
}

/** `YYYY-MM-DDTHH:mm` in the account timezone, for native date-time controls. */
export function toDateTimeParam(at: number, timeZone?: string): string {
  const date = civilDate(at, timeZone);
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(timeZone ? { timeZone } : {}),
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(at);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.year}-${pad(date.month)}-${pad(date.day)}T${pad(read('hour'))}:${pad(read('minute'))}`;
}

/** Parses a native date-time value in the account timezone, rejecting nonexistent DST wall times. */
export function fromDateTimeParam(param: unknown, timeZone?: string): number | undefined {
  if (typeof param !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(param)) return undefined;
  const [date, clock] = param.split('T');
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = clock.split(':').map(Number);
  const at = civilInstant({ year, month, day }, hour, minute, timeZone);
  return Number.isFinite(at) && toDateTimeParam(at, timeZone) === param ? at : undefined;
}
